import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ApplicationContextStore, StorageKeys, StorageService, ThemeService, TokenService } from '@raptor-fe/services';
import { ReportLinkVerification } from '@raptor-fe/core';
import { CodeLoginStore } from './store/code-login.store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'lib-code-login',
    imports: [FormsModule, ReactiveFormsModule, RouterModule],
    templateUrl: './code-login.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [CodeLoginStore],
})
export class CodeLoginComponent {
    protected readonly formGroup = new FormGroup({
        code: new FormControl('', { validators: [Validators.required], nonNullable: true }),
    });

    protected readonly registrationFormGroup = new FormGroup({
        registration: new FormControl('', { validators: [Validators.required], nonNullable: true }),
    });

    /**
     * Whether to ask the customer for the vehicle registration rather than an access code.
     *
     * True only when the link carries no usable code AND the tenant is in Registration mode. Two of
     * the three modes issue a link without a code, so the URL alone no longer identifies which
     * prompt to show and the mode has to come from the server.
     *
     * Presentation only: the server decides what it accepts from the tenant setting and the record,
     * so neither this value nor the query string can move a link between modes.
     */
    protected readonly isRegistrationChallenge = computed(
        () => this.hasNoCodeInLink() && this.store.verification() === ReportLinkVerification.Registration,
    );

    /**
     * Whether the link arrived without a usable access code, so the page has to ask for something.
     *
     * Also flipped when a carried code is auto-submitted and refused: a tenant switching to
     * Registration mode refuses the code on links it issued earlier, and retrying that same code
     * can never succeed, so the page discards it and asks for whatever the mode calls for instead.
     */
    private readonly hasNoCodeInLink = signal(false);

    protected readonly store = inject(CodeLoginStore);

    /**
     * The retailer's logo once branding has resolved, theme-aware. Null until then, and for a
     * tenant that has configured none, in which case the page renders without a logo rather than
     * showing another company's.
     */
    protected readonly currentLogoUrl = inject(ThemeService).currentLogoUrl;

    protected readonly applicationContextStore = inject(ApplicationContextStore);

    private readonly activatedRoute = inject(ActivatedRoute);

    private readonly storageService = inject(StorageService);

    private readonly tokenService = inject(TokenService);

    constructor() {
        this.activatedRoute.queryParams.pipe(takeUntilDestroyed()).subscribe(async (params) => {
            const accessCodeId = params['id'] as string | undefined;
            this.store.setAccessCodeId(accessCodeId as string);

            /**
             * Remembered before web-view mode moves storage to the session, so the id outlives
             * this tab: a customer who later lands on the report without a session is sent back
             * here to verify this link rather than to the staff sign-in. Not awaited, because
             * setIsWebViewMode must land synchronously for the application context to honour it.
             */
            if (accessCodeId) {
                void this.storageService.setString(StorageKeys.ACCESS_CODE_ID, accessCodeId);
            }

            /**
             * A link visit never continues a stored session. A leftover staff or expired token
             * would otherwise ride the bootstrap requests, be refused, and end this visit at the
             * staff sign-in before the customer has verified anything. Cleared before the storage
             * type moves, so both stores are swept, and any refusal already in flight for the old
             * token no longer matches the stored one and is ignored.
             */
            this.tokenService.removeToken();
            this.tokenService.removeRefreshToken();

            /**
             * The store may already have read the swept token at boot and still report a signed-in
             * staff user. Correcting it here keeps the interceptor from treating the unauthenticated
             * bootstrap refusals of this visit as the end of a session that no longer exists.
             */
            this.applicationContextStore.setIsLoggedIn(false);
            this.applicationContextStore.setIsWebViewMode(true);

            const accessCode = params['code'] as string | undefined;

            this.hasNoCodeInLink.set(!accessCode);

            /**
             * A link carrying a code submits itself and leaves immediately, and the report resolves
             * its own branding once authenticated. Waiting for branding here would only delay that
             * redirect, so it is loaded without blocking and the page is never held back for it.
             */
            if (accessCode) {
                void this.store.loadBranding();
                const opened = await this.store.login({ code: accessCode });

                if (!opened) {
                    this.hasNoCodeInLink.set(true);
                }
                return;
            }

            await this.store.loadBranding();
        });
    }

    async sendVerificationCode(): Promise<void> {
        if (this.store.accessCodeId()) {
            await this.store.sendAccessCode();
        }
    }

    async onSubmit(): Promise<void> {
        if (this.formGroup.invalid) {
            return;
        }
        await this.store.login({ code: this.formGroup.value.code as string });
    }

    async onSubmitRegistration(): Promise<void> {
        if (this.registrationFormGroup.invalid) {
            return;
        }
        await this.store.login({ registration: this.registrationFormGroup.value.registration as string });
    }
}
